import type { StorySlideLayerList } from '@/shared/api/models'
import type { AppDispatch, RootState } from '@/shared/store'
import type { LocalLayer } from '../../layer/types'
import type { SlideState } from '../../slide/types'
import { editorInitialized, editorMerged } from '../actions'
import type { Update } from '@reduxjs/toolkit'
import { currentLayerAdapter, originalLayerAdapter } from '../../layer/layerAdapters'

interface StorySlideData {
  id: number
  slide_number: number
  layers: readonly StorySlideLayerList[]
}

/**
 * Initialize or merge editor state from story data.
 * - Different story → full reset via editorInitialized
 * - Same story (refetch) → incremental merge via editorMerged
 */
export const initializeFromStory = (
  payload: { storyId: number; storyTitle?: string; slides: StorySlideData[] },
) => (dispatch: AppDispatch, getState: () => RootState) => {
  const { storyId, storyTitle, slides: serverSlides } = payload
  const state = getState()
  const currentStoryId = state.editor.sync.storyId

  // Different story → full re-initialization
  if (currentStoryId !== storyId) {
    const slideStates: SlideState[] = []
    const allLayers: LocalLayer[] = []

    for (const slide of serverSlides) {
      slideStates.push({
        id: slide.id,
        slideNumber: slide.slide_number,
        deletedLayerIds: [],
      })

      const layers = [...(slide.layers ?? [])] as StorySlideLayerList[]

      for (const layer of layers) {
        allLayers.push({ ...structuredClone(layer), slideId: slide.id } as LocalLayer)
      }
    }

    dispatch(editorInitialized({ storyId, storyTitle, slides: slideStates, layers: allLayers }))

    return
  }

  // Same story → per-layer merge (preserve local edits)
  const currentLayerState = state.editor.layers.current
  const originalLayerState = state.editor.layers.original
  const slidesState = state.editor.slides

  const newSlides: SlideState[] = []
  const addedLayers: LocalLayer[] = []
  const updatedCurrentLayers: Update<LocalLayer>[] = []
  const updatedOriginalLayers: LocalLayer[] = []
  const removedLayerIds: number[] = []

  for (const serverSlide of serverSlides) {
    const existingSlide = slidesState.entities[serverSlide.id]

    if (!existingSlide) {
      // New slide
      newSlides.push({
        id: serverSlide.id,
        slideNumber: serverSlide.slide_number,
        deletedLayerIds: [],
      })

      const layers = [...(serverSlide.layers ?? [])] as StorySlideLayerList[]

      for (const layer of layers) {
        addedLayers.push({ ...structuredClone(layer), slideId: serverSlide.id } as LocalLayer)
      }

      continue
    }

    const serverLayers = [...(serverSlide.layers ?? [])] as StorySlideLayerList[]
    const serverLayerIds = new Set(serverLayers.map((l) => l.id))

    for (const serverLayer of serverLayers) {
      const currentLayer = currentLayerAdapter.getSelectors().selectById(currentLayerState, serverLayer.id)
      const originalLayer = originalLayerAdapter.getSelectors().selectById(originalLayerState, serverLayer.id)

      if (!currentLayer) {
        // Layer not in local state — skip if locally deleted, otherwise add
        if (existingSlide.deletedLayerIds.includes(serverLayer.id)) continue

        addedLayers.push({ ...structuredClone(serverLayer), slideId: serverSlide.id } as LocalLayer)
        continue
      }

      const hasLocalChanges = originalLayer
        && JSON.stringify(currentLayer) !== JSON.stringify(originalLayer)

      if (!hasLocalChanges) {
        // No local changes — replace entirely from server
        updatedCurrentLayers.push({
          id: serverLayer.id,
          changes: { ...structuredClone(serverLayer), slideId: serverSlide.id } as Partial<LocalLayer>,
        })
      } else {
        // Has local changes — only update server-authoritative fields
        updatedCurrentLayers.push({
          id: serverLayer.id,
          changes: {
            url: serverLayer.url,
            image_status: serverLayer.image_status,
          } as Partial<LocalLayer>,
        })
      }

      // Always update original to track latest server state
      updatedOriginalLayers.push({ ...structuredClone(serverLayer), slideId: serverSlide.id } as LocalLayer)
    }

    // Remove layers deleted on server (but preserve temp layers with negative IDs)
    const allCurrentLayers = currentLayerAdapter.getSelectors().selectAll(currentLayerState)
    const slideCurrentLayers = allCurrentLayers.filter((l) => l.slideId === serverSlide.id)

    for (const layer of slideCurrentLayers) {
      if (layer.id > 0 && !serverLayerIds.has(layer.id)) {
        removedLayerIds.push(layer.id)
      }
    }
  }

  dispatch(editorMerged({
    newSlides,
    addedLayers,
    updatedCurrentLayers,
    updatedOriginalLayers,
    removedLayerIds,
  }))
}
